import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  connectAlpaca,
  disconnectAlpaca,
  fetchAlpacaAccount,
  fetchBrokerageConnections,
  previewAlpaca,
  setAlpacaEnabled,
  verifyAlpaca,
  type AlpacaAccountSummary,
  type BrokerageConnection,
  type BrokerageProviderInfo
} from '../services/brokerageService';
import {
  fetchDeployStrategyDashboard,
  recomputeIdealDeployMath,
  setDeployStrategyKillSwitch,
  setDeployStrategyRun,
  updateDeployStrategyRisk,
  type DeployStrategyDashboard
} from '../services/deployStrategyService';
import { patchDeployListItem } from '../services/deployListApi';
import { DeployTargetWeightCell } from '../components/DeployTargetWeightCell';

type ConnectStep = 'mode' | 'keys' | 'confirm';

function apiErr(e: unknown, fallback: string): string {
  const ax = e as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || (e instanceof Error ? e.message : fallback);
}

function money(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

function pctSigned(n: number | null | undefined, digits = 2): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

function pctTone(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n === 0) return 'text-kib-muted';
  return n > 0 ? 'text-emerald-300' : 'text-rose-300';
}

function accountLast4(account: AlpacaAccountSummary | null, alpaca: BrokerageConnection | null): string | null {
  return (
    account?.accountNumberLast4 ||
    account?.accountNumber ||
    alpaca?.accountMeta?.last4 ||
    (alpaca?.accountHint ? alpaca.accountHint.replace(/^\.+/, '') : null) ||
    null
  );
}

function EquitySparkline({ points }: { points: Array<{ t: number; equity: number | null }> }) {
  const series = points.filter((p) => p.equity != null && Number.isFinite(p.equity)) as Array<{
    t: number;
    equity: number;
  }>;
  if (series.length < 2) {
    return (
      <div className="flex h-36 items-center justify-center rounded-lg border border-white/[0.06] bg-black/20 text-xs text-kib-muted">
        Equity history appears after Alpaca paper activity.
      </div>
    );
  }
  const vals = series.map((p) => p.equity);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const w = 320;
  const h = 120;
  const pad = 8;
  const d = series
    .map((p, i) => {
      const x = pad + (i / (series.length - 1)) * (w - pad * 2);
      const y = pad + (1 - (p.equity - min) / span) * (h - pad * 2);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const up = vals[vals.length - 1] >= vals[0];
  return (
    <div className="rounded-lg border border-white/[0.06] bg-black/20 p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="text-xs text-kib-muted">Alpaca paper equity · 1M</p>
        <p className={`text-sm font-semibold tabular-nums ${up ? 'text-emerald-300' : 'text-rose-300'}`}>
          {money(vals[vals.length - 1])}
        </p>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="h-28 w-full" role="img" aria-label="Equity chart">
        <path d={d} fill="none" stroke={up ? '#6ee7b7' : '#fca5a5'} strokeWidth="2" />
      </svg>
      <p className="mt-1 text-[11px] text-kib-muted tabular-nums">
        {money(min)} – {money(max)}
      </p>
    </div>
  );
}

const DeployStrategiesPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState<BrokerageProviderInfo[]>([]);
  const [connections, setConnections] = useState<BrokerageConnection[]>([]);
  const [account, setAccount] = useState<AlpacaAccountSummary | null>(null);
  const [dash, setDash] = useState<DeployStrategyDashboard | null>(null);

  const [showConnect, setShowConnect] = useState(false);
  const [step, setStep] = useState<ConnectStep>('mode');
  const [paper, setPaper] = useState(true);
  const [apiKeyId, setApiKeyId] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [previewAccount, setPreviewAccount] = useState<AlpacaAccountSummary | null>(null);
  const [previewKeyPrefix, setPreviewKeyPrefix] = useState('');
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [confirmUseAccount, setConfirmUseAccount] = useState(false);
  const [liveAck, setLiveAck] = useState('');
  const [confirmPhrase, setConfirmPhrase] = useState('');
  const [riskMaxPct, setRiskMaxPct] = useState('10');
  const [riskMaxOrder, setRiskMaxOrder] = useState('750');
  const [riskMaxOpen, setRiskMaxOpen] = useState('5');
  const [riskHaltPct, setRiskHaltPct] = useState('3');
  const [riskDailyDeployPct, setRiskDailyDeployPct] = useState('20');
  const [riskMaxDrawdownPct, setRiskMaxDrawdownPct] = useState('15');

  const alpaca = connections.find((c) => c.provider === 'alpaca') || null;
  const provider = providers.find((p) => p.id === 'alpaca');
  const keyGuideUrl = paper
    ? provider?.keyUrlPaper || 'https://app.alpaca.markets/paper/dashboard/overview'
    : provider?.keyUrlLive || 'https://app.alpaca.markets/brokerage/dashboard/overview';

  const clearConnectForm = useCallback(() => {
    setApiKeyId('');
    setApiSecret('');
    setPreviewAccount(null);
    setPreviewKeyPrefix('');
    setPreviewWarnings([]);
    setConfirmUseAccount(false);
    setLiveAck('');
    setStep('mode');
    setPaper(true);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pack, strategyDash] = await Promise.all([
        fetchBrokerageConnections(),
        fetchDeployStrategyDashboard().catch(() => null)
      ]);
      setProviders(pack.providers);
      setConnections(pack.connections);
      setDash(strategyDash);
      if (strategyDash?.deployment) {
        setRiskMaxPct(String(strategyDash.deployment.maxPositionPct ?? 10));
        setRiskMaxOrder(String(strategyDash.deployment.maxNotionalPerOrder ?? 750));
        setRiskMaxOpen(String(strategyDash.deployment.maxOpenPositions ?? 5));
        setRiskHaltPct(String(strategyDash.deployment.dailyLossHaltPct ?? 3));
        setRiskMaxDrawdownPct(String(strategyDash.deployment.maxDrawdownPct ?? 15));
        setRiskDailyDeployPct(
          String(
            strategyDash.deployment.maxDailyDeployPct ??
              strategyDash.strategySpec?.defaults?.maxDailyDeployPct ??
              20
          )
        );
      }
      const hasAlpaca = pack.connections.some((c) => c.provider === 'alpaca');
      if (hasAlpaca) {
        try {
          const a = await fetchAlpacaAccount();
          setAccount(a.account);
          setConnections((prev) =>
            prev.map((c) => (c.provider === 'alpaca' ? a.connection : c))
          );
        } catch {
          setAccount(null);
        }
      } else {
        setAccount(null);
        setShowConnect(true);
      }
    } catch (e) {
      toast.error(apiErr(e, 'Could not load deploy workspace'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!alpaca || !dash?.live.isLive) return undefined;
    const id = window.setInterval(() => {
      void fetchDeployStrategyDashboard()
        .then(setDash)
        .catch(() => undefined);
    }, 30000);
    return () => window.clearInterval(id);
  }, [alpaca, dash?.live.isLive]);

  const startConnect = (replace = false) => {
    clearConnectForm();
    if (replace && alpaca) setPaper(alpaca.mode !== 'live');
    setShowConnect(true);
    setStep('mode');
  };

  const onPreview = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await previewAlpaca({
        apiKeyId: apiKeyId.trim(),
        apiSecret: apiSecret.trim(),
        paper
      });
      setPreviewAccount(result.account);
      setPreviewKeyPrefix(result.keyPrefix);
      setPreviewWarnings(result.warnings || []);
      setConfirmUseAccount(false);
      setLiveAck('');
      setStep('confirm');
      toast.success('Account found — confirm before saving');
    } catch (err) {
      toast.error(apiErr(err, 'Could not preview Alpaca account'));
    } finally {
      setBusy(false);
    }
  };

  const onConfirmSave = async () => {
    if (!previewAccount || !confirmUseAccount) {
      toast.error('Confirm this is the account you want for strategy execution');
      return;
    }
    if (!paper && liveAck.trim().toUpperCase() !== 'LIVE') {
      toast.error('Type LIVE to acknowledge real-money keys (execution stays paper-gated)');
      return;
    }
    const last4 = previewAccount.accountNumberLast4;
    if (!last4) {
      toast.error('Could not read account number from Alpaca');
      return;
    }
    setBusy(true);
    try {
      await connectAlpaca({
        apiKeyId: apiKeyId.trim(),
        apiSecret: apiSecret.trim(),
        paper,
        confirmLive: !paper,
        confirmAccountLast4: last4
      });
      clearConnectForm();
      setShowConnect(false);
      toast.success(`Connected Alpaca ${paper ? 'paper' : 'live'} …${last4}`);
      await load();
    } catch (err) {
      toast.error(apiErr(err, 'Could not connect Alpaca'));
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async () => {
    setBusy(true);
    try {
      const result = await verifyAlpaca();
      setAccount(result.account);
      toast.success('Credentials still valid');
      await load();
    } catch (err) {
      toast.error(apiErr(err, 'Verify failed'));
      await load();
    } finally {
      setBusy(false);
    }
  };

  const onToggleBroker = async () => {
    if (!alpaca) return;
    setBusy(true);
    try {
      await setAlpacaEnabled(!alpaca.enabled);
      toast.success(alpaca.enabled ? 'Brokerage disabled' : 'Brokerage enabled');
      await load();
    } catch (err) {
      toast.error(apiErr(err, 'Update failed'));
    } finally {
      setBusy(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm('Remove stored Alpaca credentials? Strategy execution will stop.')) return;
    setBusy(true);
    try {
      if (dash?.deployment.status === 'live') {
        await setDeployStrategyRun({ on: false });
      }
      await disconnectAlpaca();
      setAccount(null);
      clearConnectForm();
      setShowConnect(true);
      toast.success('Alpaca disconnected');
      await load();
    } catch (err) {
      toast.error(apiErr(err, 'Disconnect failed'));
    } finally {
      setBusy(false);
    }
  };

  const onGoLive = async () => {
    const phrase = dash?.confirmPhrase || 'ENABLE PAPER TRADES';
    if (confirmPhrase.trim() !== phrase) {
      toast.error(`Type ${phrase} to enable`);
      return;
    }
    setBusy(true);
    try {
      await setDeployStrategyRun({ on: true, confirmPhrase: confirmPhrase.trim() });
      setConfirmPhrase('');
      toast.success('Deploy-list Dip Sleeve is live on Alpaca paper');
      await load();
    } catch (err) {
      toast.error(apiErr(err, 'Could not enable strategy'));
    } finally {
      setBusy(false);
    }
  };

  const onPause = async () => {
    setBusy(true);
    try {
      await setDeployStrategyRun({ on: false });
      toast.success('Strategy paused — kill switch armed');
      await load();
    } catch (err) {
      toast.error(apiErr(err, 'Could not pause'));
    } finally {
      setBusy(false);
    }
  };

  const onArmKill = async () => {
    setBusy(true);
    try {
      await setDeployStrategyKillSwitch({ armed: true });
      toast.success('Kill switch armed');
      await load();
    } catch (err) {
      toast.error(apiErr(err, 'Kill switch failed'));
    } finally {
      setBusy(false);
    }
  };

  const onSaveRisk = async () => {
    const maxPositionPct = Number(riskMaxPct);
    const maxNotionalPerOrder = Number(riskMaxOrder);
    const maxOpenPositions = Number(riskMaxOpen);
    const dailyLossHaltPct = Number(riskHaltPct);
    const maxDailyDeployPct = Number(riskDailyDeployPct);
    const maxDrawdownPct = Number(riskMaxDrawdownPct);
    if (!Number.isFinite(maxPositionPct) || maxPositionPct < 1 || maxPositionPct > 50) {
      toast.error('Max % / name must be 1–50');
      return;
    }
    if (!Number.isFinite(maxNotionalPerOrder) || maxNotionalPerOrder < 5 || maxNotionalPerOrder > 50000) {
      toast.error('Max $ / order must be 5–50000');
      return;
    }
    if (
      !Number.isFinite(maxOpenPositions) ||
      maxOpenPositions < 1 ||
      maxOpenPositions > 25 ||
      Math.round(maxOpenPositions) !== maxOpenPositions
    ) {
      toast.error('Max open positions must be a whole number 1–25');
      return;
    }
    if (!Number.isFinite(dailyLossHaltPct) || dailyLossHaltPct < 0.5 || dailyLossHaltPct > 25) {
      toast.error('Daily loss halt must be 0.5–25%');
      return;
    }
    if (!Number.isFinite(maxDailyDeployPct) || maxDailyDeployPct < 5 || maxDailyDeployPct > 50) {
      toast.error('Max daily deploy % must be 5–50');
      return;
    }
    if (!Number.isFinite(maxDrawdownPct) || maxDrawdownPct < 2 || maxDrawdownPct > 50) {
      toast.error('Max drawdown must be 2–50%');
      return;
    }
    setBusy(true);
    try {
      await updateDeployStrategyRisk({
        maxPositionPct,
        maxNotionalPerOrder,
        maxOpenPositions,
        dailyLossHaltPct,
        maxDailyDeployPct,
        maxDrawdownPct
      });
      toast.success('Risk knobs saved');
      await load();
    } catch (err) {
      toast.error(apiErr(err, 'Could not save risk settings'));
    } finally {
      setBusy(false);
    }
  };

  const last4 = accountLast4(account, alpaca);
  const live = dash?.live;
  const spec = dash?.strategySpec;
  const dayPnl = (() => {
    const eq = dash?.account?.equity != null ? Number(dash.account.equity) : null;
    const start = dash?.deployment?.dayStartEquity;
    if (eq == null || start == null || start <= 0) return null;
    return ((eq - start) / start) * 100;
  })();

  return (
    <div className="mx-auto max-w-[1360px] px-4 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:px-6 sm:py-8 lg:px-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl font-bold tracking-tight text-kib-fg sm:text-3xl">Deploy Strategies</h1>
        <p className="mt-2 max-w-2xl text-sm text-kib-muted sm:text-base">
          Connect one Alpaca paper account, select the Deploy-list Dip Sleeve, go live, and watch
          performance — orders only fire on ideal dips for names on your Dashboard deploy list.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-kib-muted">Loading…</p>
      ) : (
        <div className="space-y-6">
          {/* Status strip */}
          <section className="card border-kib-cyber/20">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-mono uppercase tracking-wider text-kib-cyber/90">Strategy</p>
                <h2 className="mt-1 text-xl font-semibold text-kib-fg">
                  {spec?.name || 'Deploy-list Dip Sleeve'}
                </h2>
                <p className="mt-1 max-w-2xl text-sm text-kib-muted">
                  {spec?.summary}
                </p>
              </div>
              <span
                className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                  live?.isLive
                    ? 'bg-emerald-500/15 text-emerald-300'
                    : 'bg-amber-500/15 text-amber-200'
                }`}
              >
                {live?.label || 'Paused'}
              </span>
            </div>

            <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 text-xs">
              <div>
                <dt className="text-kib-muted">Account</dt>
                <dd className="font-medium text-kib-fg">
                  {alpaca
                    ? `Alpaca ${alpaca.mode}${last4 ? ` · …${last4}` : ''}`
                    : 'Not connected'}
                </dd>
              </div>
              <div>
                <dt className="text-kib-muted">Equity</dt>
                <dd className="font-medium text-kib-fg tabular-nums">
                  {money(dash?.account?.equity ?? account?.equity)}
                </dd>
              </div>
              <div>
                <dt className="text-kib-muted">Day P&amp;L</dt>
                <dd
                  className={`font-medium tabular-nums ${
                    dayPnl == null
                      ? 'text-kib-fg'
                      : dayPnl >= 0
                        ? 'text-emerald-300'
                        : 'text-rose-300'
                  }`}
                >
                  {dayPnl == null ? '—' : `${dayPnl >= 0 ? '+' : ''}${dayPnl.toFixed(2)}%`}
                </dd>
              </div>
              <div>
                <dt className="text-kib-muted">Deploy list</dt>
                <dd className="font-medium text-kib-fg">
                  {dash?.deployList.count ?? 0} names ·{' '}
                  {(dash?.deployList.totalTargetWeightPct ?? 0).toFixed(1)}%
                </dd>
              </div>
            </dl>

            {dash?.deployment?.autoPauseReason ? (
              <div
                role="alert"
                data-testid="strategy-auto-paused-banner"
                className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"
              >
                <p className="font-semibold">
                  Strategy auto-paused:{' '}
                  {dash.deployment.autoPauseReason === 'max_drawdown'
                    ? `max drawdown limit (${dash.deployment.maxDrawdownPct ?? 15}%) reached`
                    : dash.deployment.autoPauseReason === 'daily_loss'
                      ? `daily loss limit (${dash.deployment.dailyLossHaltPct}%) reached`
                      : dash.deployment.autoPauseReason}
                </p>
                <p className="mt-1 text-amber-100/80">
                  {dash.deployment.lastError ? `${dash.deployment.lastError}. ` : ''}
                  The kill switch is armed and no paper orders will be placed. Review your settings, then
                  re-enable with the confirm phrase (this resets the drawdown peak to current equity).
                </p>
              </div>
            ) : null}

            {live && !live.isLive && live.reasons.length > 0 ? (
              <ul className="mt-3 space-y-1 text-xs text-amber-200/90">
                {live.reasons.map((r) => (
                  <li key={r}>• {r}</li>
                ))}
              </ul>
            ) : null}

            {live?.isLive && !dash?.marketOpen ? (
              <p className="mt-3 rounded-md border border-kib-cyber/25 bg-kib-cyber/10 px-3 py-2 text-xs text-kib-fg/90">
                Armed and waiting for US regular hours (Mon–Fri 9:30–4:00 ET). Next buys fire only on{' '}
                <span className="font-mono text-kib-cyber">overreaction</span> /{' '}
                <span className="font-mono text-kib-cyber">capitulation</span> dips for deploy-list
                names — prefers 14:30–16:00 ET.
              </p>
            ) : null}
            {live?.isLive && dash?.marketOpen ? (
              <p className="mt-3 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100/90">
                Market open — monitoring deploy-list dips. Fills appear under Recent activity and in
                your email if strategy order notifications are on.
              </p>
            ) : null}

            <div className="mt-4 rounded-md border border-white/[0.06] bg-black/20 px-3 py-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                <p className="text-xs font-semibold text-kib-fg">Risk knobs</p>
                <button
                  type="button"
                  disabled={busy || !dash?.deployment}
                  onClick={() => void onSaveRisk()}
                  className="btn-secondary min-h-[44px] w-full px-3 text-sm disabled:opacity-50 sm:w-auto sm:text-xs"
                >
                  Save knobs
                </button>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
                <label className="block text-[11px] text-kib-muted">
                  Max % / name
                  <input
                    type="number"
                    inputMode="decimal"
                    min={1}
                    max={50}
                    step={0.5}
                    value={riskMaxPct}
                    onChange={(ev) => setRiskMaxPct(ev.target.value)}
                    className="mt-1 min-h-[44px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 font-mono text-base text-kib-fg sm:min-h-0 sm:py-2 sm:text-sm"
                  />
                </label>
                <label className="block text-[11px] text-kib-muted">
                  Max $ / order
                  <input
                    type="number"
                    inputMode="decimal"
                    min={5}
                    max={50000}
                    step={25}
                    value={riskMaxOrder}
                    onChange={(ev) => setRiskMaxOrder(ev.target.value)}
                    className="mt-1 min-h-[44px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 font-mono text-base text-kib-fg sm:min-h-0 sm:py-2 sm:text-sm"
                  />
                </label>
                <label className="block text-[11px] text-kib-muted">
                  Max open positions
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={25}
                    step={1}
                    value={riskMaxOpen}
                    onChange={(ev) => setRiskMaxOpen(ev.target.value)}
                    className="mt-1 min-h-[44px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 font-mono text-base text-kib-fg sm:min-h-0 sm:py-2 sm:text-sm"
                  />
                </label>
                <label className="block text-[11px] text-kib-muted">
                  Daily loss halt %
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0.5}
                    max={25}
                    step={0.5}
                    value={riskHaltPct}
                    onChange={(ev) => setRiskHaltPct(ev.target.value)}
                    className="mt-1 min-h-[44px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 font-mono text-base text-kib-fg sm:min-h-0 sm:py-2 sm:text-sm"
                  />
                </label>
                <label className="block text-[11px] text-kib-muted">
                  Max drawdown %
                  <input
                    type="number"
                    inputMode="decimal"
                    min={2}
                    max={50}
                    step={1}
                    value={riskMaxDrawdownPct}
                    onChange={(ev) => setRiskMaxDrawdownPct(ev.target.value)}
                    className="mt-1 min-h-[44px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 font-mono text-base text-kib-fg sm:min-h-0 sm:py-2 sm:text-sm"
                  />
                </label>
                <label className="block text-[11px] text-kib-muted sm:col-span-1">
                  Max daily deploy %
                  <input
                    type="number"
                    inputMode="decimal"
                    min={5}
                    max={50}
                    step={1}
                    value={riskDailyDeployPct}
                    onChange={(ev) => setRiskDailyDeployPct(ev.target.value)}
                    className="mt-1 min-h-[44px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 font-mono text-base text-kib-fg sm:min-h-0 sm:py-2 sm:text-sm"
                  />
                </label>
              </div>
              <p className="mt-2 text-[10px] leading-relaxed text-kib-muted sm:text-[11px]">
                Applied on the next paper buy. Daily loss halt (vs opening balance) and max drawdown
                (peak-to-trough equity) are checked every few minutes and auto-pause the strategy. Max daily deploy caps new buy notional vs equity per session. Deploy-list names
                keep full size on market-wide flushes (your research is the quality gate).
              </p>
            </div>

            {dash?.deployment?.lastError && !dash.deployment.autoPauseReason ? (
              <p className="mt-3 text-xs text-rose-300">{dash.deployment.lastError}</p>
            ) : null}
            {dash?.deployment?.lastSkipReason ? (
              <p className="mt-2 text-xs text-kib-muted">
                Last skip: {dash.deployment.lastSkipReason}
              </p>
            ) : null}

            <div className="mt-5 flex flex-col gap-3 border-t border-white/[0.06] pt-4 sm:flex-row sm:flex-wrap sm:items-end">
              {dash?.deployment?.status === 'live' && !dash.deployment.killSwitchArmed ? (
                <>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onPause()}
                    className="btn-secondary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
                  >
                    Pause strategy
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onArmKill()}
                    className="min-h-[44px] w-full px-3 text-sm text-rose-300 hover:underline disabled:opacity-50 sm:w-auto"
                  >
                    Arm kill switch
                  </button>
                </>
              ) : (
                <>
                  <label className="block w-full text-xs text-kib-muted sm:min-w-[220px] sm:w-auto">
                    Type <span className="font-mono text-kib-fg">{dash?.confirmPhrase || 'ENABLE PAPER TRADES'}</span>
                    <input
                      type="text"
                      autoComplete="off"
                      value={confirmPhrase}
                      onChange={(ev) => setConfirmPhrase(ev.target.value)}
                      className="mt-1 min-h-[44px] w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 text-base text-kib-fg font-mono sm:min-h-0 sm:py-2 sm:text-sm"
                      placeholder={dash?.confirmPhrase || 'ENABLE PAPER TRADES'}
                    />
                  </label>
                  <button
                    type="button"
                    disabled={busy || !alpaca || alpaca.mode !== 'paper'}
                    onClick={() => void onGoLive()}
                    className="btn-primary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
                  >
                    Go live (Alpaca paper)
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => void load()}
                className="btn-secondary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
              >
                Refresh
              </button>
            </div>
            {alpaca && alpaca.mode !== 'paper' ? (
              <p className="mt-3 text-xs text-amber-200">
                Live brokerage keys are connected — strategy execution is paper-only. Switch to paper keys
                to enable.
              </p>
            ) : null}
          </section>

          {/* Performance + positions */}
          {alpaca ? (
            <div className="grid gap-6 lg:grid-cols-2">
              <section className="card">
                <h2 className="text-lg font-semibold text-kib-fg mb-3">Performance</h2>
                <EquitySparkline points={dash?.portfolioHistory?.points || []} />
                <p className="mt-2 text-xs text-kib-muted">
                  Chart from Alpaca paper portfolio history for account
                  {last4 ? ` …${last4}` : ''}. Strategy does not trade live cash.
                </p>
              </section>
              <section className="card">
                <h2 className="text-lg font-semibold text-kib-fg mb-3">Open positions</h2>
                {(dash?.positions?.length || 0) === 0 ? (
                  <p className="text-sm text-kib-muted">
                    No open Alpaca paper positions yet
                    {live?.isLive && !dash?.marketOpen
                      ? ' — waiting for the next RTH dip fill.'
                      : '.'}
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-[10px] uppercase tracking-wide text-kib-muted">
                        <tr>
                          <th className="py-2 text-left">Symbol</th>
                          <th className="py-2 text-right">Qty</th>
                          <th className="py-2 text-right">Value</th>
                          <th className="py-2 text-right">Avg</th>
                          <th className="py-2 text-right">uP/L</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/[0.06]">
                        {dash?.positions.map((p) => (
                          <tr key={p.symbol}>
                            <td className="py-2">
                              <Link
                                to={`/charts?symbol=${encodeURIComponent(p.symbol)}`}
                                className="font-mono font-semibold text-kib-fg hover:text-kib-cyber"
                              >
                                {p.symbol}
                              </Link>
                            </td>
                            <td className="py-2 text-right tabular-nums">{p.qty ?? '—'}</td>
                            <td className="py-2 text-right tabular-nums">{money(p.marketValue)}</td>
                            <td className="py-2 text-right tabular-nums text-kib-muted">
                              {money(p.avgEntryPrice)}
                            </td>
                            <td
                              className={`py-2 text-right tabular-nums ${pctTone(
                                p.unrealizedPlpc != null ? p.unrealizedPlpc * 100 : p.unrealizedPl
                              )}`}
                            >
                              {p.unrealizedPl != null ? money(p.unrealizedPl) : '—'}
                              {p.unrealizedPlpc != null ? (
                                <span className="block text-[10px]">
                                  {pctSigned(p.unrealizedPlpc * 100)}
                                </span>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </div>
          ) : null}

          {/* Deploy list mirror */}
          <section className="card">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="text-lg font-semibold text-kib-fg">Deploy list (universe)</h2>
                <p className="mt-1 text-sm text-kib-muted">
                  Only these Dashboard names can receive buys. Add or remove via the watchlist{' '}
                  <strong className="text-kib-fg">Deploy</strong> checkbox ·{' '}
                  <Link to="/dashboard" className="text-kib-cyber underline-offset-2 hover:underline">
                    Open Dashboard
                  </Link>
                  . Names stay on the list even if Alpaca cannot trade them — we warn below and skip
                  those buys. Target % auto-refreshes weekdays ~8:30 ET (inverse-vol ideals).
                </p>
              </div>
              <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
                <button
                  type="button"
                  disabled={busy}
                  className="btn-secondary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
                  title="Also runs automatically weekdays ~8:30 ET"
                  onClick={() => {
                    void (async () => {
                      setBusy(true);
                      try {
                        const r = await recomputeIdealDeployMath({ applyBaselines: false });
                        const pinned = Number(r.pinnedSkipped) || 0;
                        toast.success(
                          pinned > 0
                            ? `Updated ideals on ${r.updated} name(s) · ${pinned} pinned kept`
                            : `Updated ideal weights on ${r.updated} name(s)`
                        );
                        await load();
                      } catch (err) {
                        toast.error(apiErr(err, 'Recompute failed'));
                      } finally {
                        setBusy(false);
                      }
                    })();
                  }}
                >
                  Recompute ideal weights
                </button>
                <button
                  type="button"
                  disabled={busy}
                  className="btn-secondary min-h-[44px] w-full text-sm disabled:opacity-50 sm:w-auto"
                  onClick={() => {
                    if (
                      !window.confirm(
                        'Apply ideal baselines to deploy-list alerts? This rewrites baseline prices used for dip detection.'
                      )
                    ) {
                      return;
                    }
                    void (async () => {
                      setBusy(true);
                      try {
                        const r = await recomputeIdealDeployMath({ applyBaselines: true });
                        toast.success(
                          `Weights + baselines updated on ${r.updated} name(s)`
                        );
                        await load();
                      } catch (err) {
                        toast.error(apiErr(err, 'Baseline apply failed'));
                      } finally {
                        setBusy(false);
                      }
                    })();
                  }}
                >
                  Apply ideal baselines
                </button>
              </div>
            </div>
            {(dash?.deployList.untradableCount || 0) > 0 ? (
              <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100/95">
                <strong className="font-semibold">
                  {dash?.deployList.untradableCount} name
                  {(dash?.deployList.untradableCount || 0) === 1 ? '' : 's'} cannot be traded
                </strong>{' '}
                via your connected Alpaca paper API. They stay on the list for monitoring; the sleeve
                will skip paper buys for those symbols until Alpaca marks them tradable.
              </div>
            ) : null}

            {(dash?.deployList.items.length || 0) === 0 ? (
              <p className="text-sm text-kib-muted">
                Empty — strategy will not place orders until you add US stocks to the deploy list.
              </p>
            ) : (
              <>
                {/* Mobile cards */}
                <ul className="space-y-2 md:hidden">
                  {dash?.deployList.items.map((it) => (
                    <li
                      key={it.id}
                      className="rounded-md border border-white/[0.06] bg-black/20 px-3 py-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={`/charts?symbol=${encodeURIComponent(it.symbol)}`}
                          className="min-h-[44px] inline-flex items-center font-mono text-base font-semibold text-kib-fg hover:text-kib-cyber"
                        >
                          {it.symbol}
                        </Link>
                        {it.brokerTradable === false ? (
                          <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-medium text-amber-200">
                            Can&apos;t deploy
                          </span>
                        ) : it.brokerTradable === true ? (
                          <span className="shrink-0 text-[10px] text-emerald-400/90">Tradable</span>
                        ) : null}
                      </div>
                      {it.brokerTradable === false && it.brokerTradeNote ? (
                        <p className="mt-1 text-[10px] leading-snug text-amber-200/80">
                          {it.brokerTradeNote}
                        </p>
                      ) : null}
                      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                        <div>
                          <dt className="text-kib-muted">Target</dt>
                          <dd>
                            <DeployTargetWeightCell
                              symbol={it.symbol}
                              targetWeightPct={it.targetWeightPct}
                              weightPinned={Boolean(it.weightPinned)}
                              disabled={busy}
                              onSave={async (input) => {
                                await patchDeployListItem(it.userAlertId, input);
                                await load();
                                toast.success(
                                  input.targetWeightPct != null
                                    ? 'Target % saved & pinned'
                                    : input.weightPinned
                                      ? 'Pinned'
                                      : 'Unpinned'
                                );
                              }}
                            />
                          </dd>
                        </div>
                        <div>
                          <dt className="text-kib-muted">Actual</dt>
                          <dd className="font-mono tabular-nums text-kib-fg">
                            {it.actualWeightPct != null ? `${it.actualWeightPct}%` : '—'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-kib-muted">Owned</dt>
                          <dd className="font-mono tabular-nums text-kib-fg">
                            {it.position ? money(it.position.marketValue) : '—'}
                            {it.position?.qty != null ? (
                              <span className="block text-[10px] text-kib-muted">
                                {it.position.qty} sh
                              </span>
                            ) : null}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-kib-muted">uP/L</dt>
                          <dd
                            className={`font-mono tabular-nums ${pctTone(it.position?.unrealizedPl)}`}
                          >
                            {it.position?.unrealizedPl != null
                              ? money(it.position.unrealizedPl)
                              : '—'}
                            {it.position?.unrealizedPlpc != null ? (
                              <span className="block text-[10px]">
                                {pctSigned(it.position.unrealizedPlpc)}
                              </span>
                            ) : null}
                          </dd>
                        </div>
                        <div className="col-span-2">
                          <dt className="text-kib-muted">Limit band</dt>
                          <dd className="font-mono tabular-nums text-kib-muted">
                            {it.suggestedLimitMin != null && it.suggestedLimitMax != null
                              ? `${money(it.suggestedLimitMin)} – ${money(it.suggestedLimitMax)}`
                              : '—'}
                          </dd>
                        </div>
                      </dl>
                    </li>
                  ))}
                </ul>
                {/* Desktop table */}
                <div className="hidden overflow-x-auto rounded-md border border-white/[0.06] md:block">
                  <table className="w-full min-w-[720px] text-sm">
                    <thead className="bg-kib-surface/80 text-[10px] uppercase tracking-wide text-kib-muted">
                      <tr>
                        <th className="px-3 py-2 text-left">Symbol</th>
                        <th className="px-3 py-2 text-left">Alpaca</th>
                        <th className="px-3 py-2 text-right">Target %</th>
                        <th className="px-3 py-2 text-right">Owned</th>
                        <th className="px-3 py-2 text-right">Actual %</th>
                        <th className="px-3 py-2 text-right">uP/L</th>
                        <th className="px-3 py-2 text-left">Limit band</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06]">
                      {dash?.deployList.items.map((it) => (
                        <tr key={it.id}>
                          <td className="px-3 py-2">
                            <Link
                              to={`/charts?symbol=${encodeURIComponent(it.symbol)}`}
                              className="font-mono font-semibold text-kib-fg hover:text-kib-cyber"
                            >
                              {it.symbol}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {it.brokerTradable === false ? (
                              <span
                                className="rounded bg-amber-500/15 px-1.5 py-0.5 font-medium text-amber-200"
                                title={it.brokerTradeNote || 'Not tradable on Alpaca'}
                              >
                                Can&apos;t deploy
                              </span>
                            ) : it.brokerTradable === true ? (
                              <span className="text-emerald-400/90">Tradable</span>
                            ) : (
                              <span className="text-kib-muted">—</span>
                            )}
                            {it.brokerTradable === false && it.brokerTradeNote ? (
                              <span className="mt-0.5 block max-w-[180px] text-[10px] leading-snug text-amber-200/80">
                                {it.brokerTradeNote}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <DeployTargetWeightCell
                              symbol={it.symbol}
                              targetWeightPct={it.targetWeightPct}
                              weightPinned={Boolean(it.weightPinned)}
                              disabled={busy}
                              onSave={async (input) => {
                                await patchDeployListItem(it.userAlertId, input);
                                await load();
                                toast.success(
                                  input.targetWeightPct != null
                                    ? 'Target % saved & pinned'
                                    : input.weightPinned
                                      ? 'Pinned'
                                      : 'Unpinned'
                                );
                              }}
                            />
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-kib-muted">
                            {it.position ? (
                              <>
                                <span className="text-kib-fg">{money(it.position.marketValue)}</span>
                                {it.position.qty != null ? (
                                  <span className="block text-[10px]">{it.position.qty} sh</span>
                                ) : null}
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-kib-muted">
                            {it.actualWeightPct != null ? `${it.actualWeightPct}%` : '—'}
                          </td>
                          <td
                            className={`px-3 py-2 text-right tabular-nums ${pctTone(it.position?.unrealizedPl)}`}
                          >
                            {it.position?.unrealizedPl != null
                              ? money(it.position.unrealizedPl)
                              : '—'}
                            {it.position?.unrealizedPlpc != null ? (
                              <span className="block text-[10px]">
                                {pctSigned(it.position.unrealizedPlpc)}
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-xs tabular-nums text-kib-muted">
                            {it.suggestedLimitMin != null && it.suggestedLimitMax != null
                              ? `${money(it.suggestedLimitMin)} – ${money(it.suggestedLimitMax)}`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          {/* How it works */}
          <section className="card">
            <h2 className="text-lg font-semibold text-kib-fg mb-2">How this strategy works</h2>
            <ul className="list-disc space-y-1.5 pl-5 text-sm text-kib-muted">
              {(spec?.rules || []).map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ul>
            {spec?.quant?.notes?.length ? (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-kib-cyber/90">
                {spec.quant.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            ) : null}
            <p className="mt-3 text-xs text-kib-muted">
              Risk defaults: max {spec?.defaults.maxPositionPct ?? 10}% / name · max $
              {spec?.defaults.maxNotionalPerOrder ?? 750} / order · max{' '}
              {spec?.defaults.maxOpenPositions ?? 5} positions · daily loss halt −
              {spec?.defaults.dailyLossHaltPct ?? 3}% (auto-pauses). Tier fill:{' '}
              overreaction {(spec?.quant?.tierFillFraction?.overreaction ?? 0.55) * 100}% →
              capitulation {(spec?.quant?.tierFillFraction?.capitulation ?? 1) * 100}% of deploy
              weight.
            </p>
          </section>

          {/* Orders */}
          <section className="card">
            <h2 className="text-lg font-semibold text-kib-fg mb-3">Recent activity</h2>
            {(dash?.recentOrders.length || 0) === 0 ? (
              <p className="text-sm text-kib-muted">
                No strategy orders yet. When live, overreaction/capitulation dips on deploy-list names
                create Alpaca paper buys (prefers 14:30–16:00 ET; capitulation may probe earlier).
                You get an email on each successful order if enabled in Profile.
              </p>
            ) : (
              <>
                <ul className="space-y-2 md:hidden">
                  {dash?.recentOrders.map((o) => (
                    <li
                      key={o.id}
                      className="rounded-md border border-white/[0.06] bg-black/20 px-3 py-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={`/charts?symbol=${encodeURIComponent(o.symbol)}`}
                          className="min-h-[44px] inline-flex items-center font-mono text-base font-semibold text-kib-fg hover:text-kib-cyber"
                        >
                          {o.symbol}
                        </Link>
                        <span className="shrink-0 text-[10px] text-kib-muted">
                          {new Date(o.createdAt).toLocaleString()}
                        </span>
                      </div>
                      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                        <div>
                          <dt className="text-kib-muted">Tier</dt>
                          <dd className="text-kib-fg">{o.dipTier || '—'}</dd>
                        </div>
                        <div>
                          <dt className="text-kib-muted">Size</dt>
                          <dd className="font-mono tabular-nums text-kib-fg">
                            {o.notional != null
                              ? money(o.notional)
                              : o.qty != null
                                ? `${o.qty} sh`
                                : '—'}
                          </dd>
                        </div>
                        <div className="col-span-2">
                          <dt className="text-kib-muted">Status</dt>
                          <dd className="text-kib-fg">
                            {o.status}
                            {o.skipReason ? (
                              <span className="mt-0.5 block text-kib-muted">{o.skipReason}</span>
                            ) : null}
                          </dd>
                        </div>
                      </dl>
                    </li>
                  ))}
                </ul>
                <div className="hidden overflow-x-auto rounded-md border border-white/[0.06] md:block">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead className="bg-kib-surface/80 text-[10px] uppercase tracking-wide text-kib-muted">
                      <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-left">Symbol</th>
                        <th className="px-3 py-2 text-left">Tier</th>
                        <th className="px-3 py-2 text-right">Size</th>
                        <th className="px-3 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/[0.06]">
                      {dash?.recentOrders.map((o) => (
                        <tr key={o.id}>
                          <td className="px-3 py-2 text-xs text-kib-muted whitespace-nowrap">
                            {new Date(o.createdAt).toLocaleString()}
                          </td>
                          <td className="px-3 py-2">
                            <Link
                              to={`/charts?symbol=${encodeURIComponent(o.symbol)}`}
                              className="font-mono font-semibold text-kib-fg hover:text-kib-cyber"
                            >
                              {o.symbol}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs text-kib-muted">{o.dipTier || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-xs">
                            {o.notional != null
                              ? money(o.notional)
                              : o.qty != null
                                ? `${o.qty} sh`
                                : '—'}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <span className="text-kib-fg">{o.status}</span>
                            {o.skipReason ? (
                              <span className="block text-kib-muted">{o.skipReason}</span>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          {/* Brokerage account card */}
          {alpaca ? (
            <section className="card">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <h2 className="text-lg font-semibold text-kib-fg">Brokerage connection</h2>
                  <p className="mt-1 text-sm text-kib-muted">
                    Execution account for this strategy · keys encrypted · never shown after save
                  </p>
                </div>
                <span className="rounded-md px-2.5 py-1 text-xs font-medium bg-white/[0.06] text-kib-muted">
                  {alpaca.enabled ? alpaca.status : 'disabled'} · {alpaca.mode}
                </span>
              </div>
              <p className="text-sm text-kib-fg">
                Key <span className="font-mono">{alpaca.keyPrefix}</span>
                {alpaca.accountHint ? (
                  <span className="text-kib-muted"> · account {alpaca.accountHint}</span>
                ) : null}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onVerify()}
                  className="btn-secondary min-h-[44px] text-sm disabled:opacity-50"
                >
                  Re-verify
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onToggleBroker()}
                  className="btn-secondary min-h-[44px] text-sm disabled:opacity-50"
                >
                  {alpaca.enabled ? 'Disable brokerage' : 'Enable brokerage'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => startConnect(true)}
                  className="btn-secondary min-h-[44px] text-sm disabled:opacity-50"
                >
                  Change account / keys
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onDisconnect()}
                  className="min-h-[44px] px-2 text-sm text-rose-300 hover:underline disabled:opacity-50"
                >
                  Disconnect
                </button>
              </div>
            </section>
          ) : null}

          {/* Connect wizard */}
          {showConnect || !alpaca ? (
            <section className="card">
              <div className="mb-5">
                <h2 className="text-xl font-semibold text-kib-fg">
                  {alpaca ? 'Change brokerage account' : 'Connect brokerage account'}
                </h2>
                <p className="mt-1 text-sm text-kib-muted max-w-2xl">
                  Use <strong className="text-kib-fg">paper</strong> keys for strategy execution. We preview
                  the exact account before saving.
                </p>
                <ol className="mt-4 flex flex-wrap gap-2 text-xs font-medium">
                  {(
                    [
                      ['mode', '1. Environment'],
                      ['keys', '2. API keys'],
                      ['confirm', '3. Confirm account']
                    ] as const
                  ).map(([id, label]) => (
                    <li
                      key={id}
                      className={`rounded-md px-2.5 py-1 ${
                        step === id ? 'bg-kib-cyber/15 text-kib-cyber' : 'bg-white/[0.04] text-kib-muted'
                      }`}
                    >
                      {label}
                    </li>
                  ))}
                </ol>
              </div>

              {step === 'mode' ? (
                <div className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setPaper(true)}
                      className={`rounded-lg border p-4 text-left transition-colors ${
                        paper
                          ? 'border-kib-cyber/50 bg-kib-cyber/10'
                          : 'border-white/[0.08] bg-black/20 hover:border-white/20'
                      }`}
                    >
                      <p className="font-semibold text-kib-fg">Paper (required for strategy)</p>
                      <p className="mt-1 text-xs text-kib-muted">
                        Simulated funds. Deploy Strategies only executes against paper today.
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaper(false)}
                      className={`rounded-lg border p-4 text-left transition-colors ${
                        !paper
                          ? 'border-amber-400/50 bg-amber-500/10'
                          : 'border-white/[0.08] bg-black/20 hover:border-white/20'
                      }`}
                    >
                      <p className="font-semibold text-kib-fg">Live keys</p>
                      <p className="mt-1 text-xs text-kib-muted">
                        You may store live keys, but the strategy will refuse to place live orders.
                      </p>
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn-primary min-h-[44px] text-sm" onClick={() => setStep('keys')}>
                      Continue
                    </button>
                    {alpaca ? (
                      <button
                        type="button"
                        className="btn-secondary min-h-[44px] text-sm"
                        onClick={() => {
                          clearConnectForm();
                          setShowConnect(false);
                        }}
                      >
                        Cancel
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {step === 'keys' ? (
                <form onSubmit={(e) => void onPreview(e)} className="space-y-4">
                  <a
                    href={keyGuideUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex text-sm text-kib-cyber underline-offset-2 hover:underline"
                  >
                    Open Alpaca {paper ? 'paper' : 'live'} dashboard →
                  </a>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="block text-xs text-kib-muted">
                      API Key ID
                      <input
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        value={apiKeyId}
                        onChange={(ev) => setApiKeyId(ev.target.value.replace(/\s+/g, ''))}
                        className="mt-1 w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 text-base text-kib-fg font-mono sm:py-2 sm:text-sm"
                        placeholder={paper ? 'PK…' : 'AK…'}
                        required
                      />
                    </label>
                    <label className="block text-xs text-kib-muted">
                      Secret Key
                      <input
                        type="password"
                        autoComplete="new-password"
                        value={apiSecret}
                        onChange={(ev) => setApiSecret(ev.target.value.replace(/\s+/g, ''))}
                        className="mt-1 w-full rounded-md border border-white/[0.1] bg-black/30 px-3 py-2.5 text-base text-kib-fg font-mono sm:py-2 sm:text-sm"
                        placeholder="••••••••"
                        required
                      />
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn-secondary min-h-[44px] text-sm" onClick={() => setStep('mode')}>
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={busy || !apiKeyId || !apiSecret}
                      className="btn-primary min-h-[44px] text-sm disabled:opacity-50"
                    >
                      {busy ? 'Checking…' : 'Preview account'}
                    </button>
                  </div>
                </form>
              ) : null}

              {step === 'confirm' && previewAccount ? (
                <div className="space-y-4">
                  <div
                    className={`rounded-lg border p-4 ${
                      paper ? 'border-kib-cyber/30 bg-kib-cyber/5' : 'border-amber-400/35 bg-amber-500/5'
                    }`}
                  >
                    <p className="text-xs font-mono uppercase tracking-wider text-kib-muted">
                      Verified Alpaca account
                    </p>
                    <p className="mt-1 text-lg font-semibold text-kib-fg">
                      {paper ? 'Paper' : 'Live'} · …{previewAccount.accountNumberLast4 || '????'}
                    </p>
                    <p className="mt-1 text-xs text-kib-muted font-mono">Key {previewKeyPrefix}</p>
                    <dl className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 text-xs">
                      <div>
                        <dt className="text-kib-muted">Status</dt>
                        <dd className="font-medium text-kib-fg">{previewAccount.status || '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-kib-muted">Equity</dt>
                        <dd className="font-medium text-kib-fg">{money(previewAccount.equity)}</dd>
                      </div>
                      <div>
                        <dt className="text-kib-muted">Buying power</dt>
                        <dd className="font-medium text-kib-fg">{money(previewAccount.buyingPower)}</dd>
                      </div>
                      <div>
                        <dt className="text-kib-muted">Cash</dt>
                        <dd className="font-medium text-kib-fg">{money(previewAccount.cash)}</dd>
                      </div>
                    </dl>
                    {previewWarnings.length > 0 ? (
                      <ul className="mt-3 space-y-1 text-xs text-amber-200">
                        {previewWarnings.map((w) => (
                          <li key={w}>• {w}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                  <label className="flex min-h-[44px] items-start gap-2 text-sm text-kib-fg">
                    <input
                      type="checkbox"
                      checked={confirmUseAccount}
                      onChange={(ev) => setConfirmUseAccount(ev.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-white/20"
                    />
                    <span>
                      Use this account for Deploy Strategies execution (paper orders only until live is
                      unlocked).
                    </span>
                  </label>
                  {!paper ? (
                    <label className="block text-xs text-amber-200/90">
                      Type <span className="font-mono font-semibold">LIVE</span> to store live keys
                      <input
                        type="text"
                        autoComplete="off"
                        value={liveAck}
                        onChange={(ev) => setLiveAck(ev.target.value)}
                        className="mt-1 w-full max-w-xs rounded-md border border-amber-400/40 bg-black/30 px-3 py-2.5 text-base text-kib-fg font-mono uppercase sm:py-2 sm:text-sm"
                        placeholder="LIVE"
                      />
                    </label>
                  ) : null}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-secondary min-h-[44px] text-sm"
                      disabled={busy}
                      onClick={() => {
                        setStep('keys');
                        setPreviewAccount(null);
                      }}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      disabled={
                        busy ||
                        !confirmUseAccount ||
                        (!paper && liveAck.trim().toUpperCase() !== 'LIVE')
                      }
                      onClick={() => void onConfirmSave()}
                      className="btn-primary min-h-[44px] text-sm disabled:opacity-50"
                    >
                      {busy ? 'Saving…' : 'Encrypt & save connection'}
                    </button>
                  </div>
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default DeployStrategiesPage;
